import { PublicThemeProperties } from '@/common/AppTheme';

interface TenantSetting {
  id: number;
  name: string;
  value: string;
  isClientSettings: boolean;
}

export interface TenantData {
  tenantId: number;
  tenantName: string;
  tenantCode: string;
  externalId: string;
  domains: string[];
  settings: TenantSetting[];
}

export class Tenant {
  private tenantData: TenantData;

  constructor(tenantData: TenantData) {
    this.tenantData = tenantData;
  }

  get tenantName(): string {
    return this.tenantData.tenantName;
  }

  get tenantCode(): string {
    return this.tenantData.tenantCode;
  }

  get externalId(): string {
    return this.tenantData.externalId;
  }

  get settings(): TenantSetting[] {
    return this.tenantData.settings;
  }

  get themeProperties(): PublicThemeProperties | undefined {
    const themeSetting = this.settings.find((setting: TenantSetting) => setting.name === 'Client.Theme.Properties');

    if (!themeSetting) {
      return undefined;
    }

    return JSON.parse(themeSetting.value) as PublicThemeProperties;
  }
}

export interface ITenantLoader {
  getTenantForHost(host: string): Promise<Tenant | undefined>;
}

export class StaticTenantLoader implements ITenantLoader {
  tenants: TenantData[] = [
    {
      tenantId: 1000,
      tenantName: 'OnSend-devtest',
      tenantCode: '',
      externalId: 'org_YI7SFVOmL9DAtr9Z',
      domains: ['localhost', 'track-local.onsend.com', 'track-dev.onsend.com', 'track-test.onsend.com'],
      settings: []
    },

    {
      tenantId: 1001,
      tenantName: 'DragonFreight-devtest',
      tenantCode: '',
      externalId: 'org_nZSFcRT5Wny4EL2m',
      domains: ['track-local.dragon-freight.com', 'track-dev.dragon-freight.com', 'track-test.dragon-freight.com'],
      settings: []
    },

    {
      tenantId: 1002,
      tenantName: 'OnSend',
      tenantCode: '',
      externalId: 'org_J143vxecuSAqCmjo',
      domains: ['track.onsend.com'],
      settings: []
    },

    {
      tenantId: 1003,
      tenantName: 'DragonFreight',
      tenantCode: '',
      externalId: 'org_X3RcDMUYo1rvZhuK',
      domains: ['track.dragon-freight.com'],
      settings: []
    }
  ];

  async getTenantForHost(host: string): Promise<Tenant | undefined> {
    const tenantData = this.tenants.find((tenant) => tenant.domains.includes(host));

    if (!tenantData) {
      return undefined;
    }

    return new Tenant(tenantData);
  }
}

export class ConfigurationServiceTenantLoader implements ITenantLoader {
  configurationServiceApiUrl: string;

  constructor(configurationServiceApiUrl: string) {
    this.configurationServiceApiUrl = configurationServiceApiUrl;
  }

  async getTenantForHost(host: string): Promise<Tenant | undefined> {
    const tenantConfigPath = `/api/v1/configuration/tenants/${host}/clientSettings`;
    const tenantResult = await fetch(`${this.configurationServiceApiUrl}${tenantConfigPath}`);
    const tenantData = (await tenantResult.json()) as TenantData;

    return new Tenant(tenantData);
  }
}
